import React, { useEffect, useState } from 'react';
import { colors } from '../../styleSheet';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Modal,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FILTER_INPUT_SX, UI } from './tokens';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { DebounceInput } from 'react-debounce-input';
import { getAllChainsFromSessionData, utcToday } from './Utils';

import PipelineMonitoringTable from '../../redux/containers/pipelineMonitoring/PipelineMonitoringTable';
import AlertIndicatorChip from './AlertIndicatorChip';
import UtcClock from './UtcClock';
import PipelineAlerts from './PipelineAlerts';
import SessionAlert from './SessionAlert';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  height: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  zIndex: 100,
  borderRadius: 2,
  overflow: 'auto',
  outline: 0,
};

export default function PipelineMonitoring(props) {
  // Default to the current UTC date (session dates are UTC), which can differ
  // from the browser's local date around midnight.
  const [selectedDate, setSelectedDate] = useState(utcToday());
  const [filterText, setFilterText] = useState('');
  const [selectedChains, setSelectedChains] = useState([]);
  const {
    getAllMetadata,
    sessionProgressTableData,
    getAlerts,
    alerts,
    loadingNavigationData,
  } = props;

  // The list of session ids for the selected date — derived from the single
  // session-progress-table feed (the table component fetches/polls it), used here only
  // to scope alert badges to sessions on screen.
  const sessionIds = Object.keys(sessionProgressTableData || {});

  // Service-level alerts (no session_id) vs. session-level alerts scoped to sessions on
  // screen. Session alerts stay hidden until navigation data has loaded to avoid flashing
  // a count against a still-incomplete session list.
  const pipelineAlertsCount = (alerts || []).filter(
    (alertInfo) => !alertInfo.incident.metric?.labels.session_id
  ).length;

  const sessionAlertsCount =
    loadingNavigationData || !alerts
      ? 0
      : alerts.filter(
          (alertInfo) =>
            alertInfo?.incident.metric.labels.session_id &&
            sessionIds.includes(alertInfo?.incident.metric.labels.session_id)
        ).length;

  const [sessionAlertsOpen, setSessionAlertsOpen] = useState(false);
  const [serviceAlertsOpen, setServiceAlertsOpen] = useState(false);

  const [showTestSessions, setShowTestSessions] = useState(false);

  // Session progress (jobs, batches, navigation) is fetched and polled by the
  // PipelineMonitoringTable component from the single /session_progress_table feed, so the
  // page no longer fetches the granular jobs/batches/navigation endpoints itself.

  //For getting metadata on load
  useEffect(() => {
    getAllMetadata({ chain_code: null });
  }, [getAllMetadata]);

  useEffect(() => {
    getAlerts({});
  }, []);

  return (
    <Box sx={{ backgroundColor: colors.slate100, minHeight: '100vh', pt: 1, pb: 4 }}>
      {serviceAlertsOpen || sessionAlertsOpen ? (
        <Modal
          style={{ backdropFilter: 'blur(5px)' }}
          open={serviceAlertsOpen || sessionAlertsOpen}
          onClose={() => {
            setServiceAlertsOpen(false);
            setSessionAlertsOpen(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 'bold' }}
            >
              {serviceAlertsOpen ? 'Service Alerts' : 'Session Alerts'}
            </Typography>

            {serviceAlertsOpen ? (
              <PipelineAlerts alerts={alerts} />
            ) : (
              <Box>
                {alerts
                  .filter(
                    (alertInfo) =>
                      alertInfo?.incident.metric?.labels.session_id &&
                      sessionIds.includes(
                        alertInfo.incident.metric.labels.session_id
                      )
                  )
                  .map((alertInfo) => (
                    <SessionAlert alertInfo={alertInfo} date={selectedDate} />
                  ))}
              </Box>
            )}
          </Box>
        </Modal>
      ) : null}

      <Box>
        <Box sx={{ marginX: 4, marginY: 1 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator={
              <NavigateNextRoundedIcon
                sx={{ fontSize: 18, color: UI.textMuted }}
              />
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: 14,
                fontWeight: 700,
                color: UI.textPrimary,
              }}
            >
              <AccountTreeRoundedIcon
                sx={{ fontSize: 18, color: UI.textSecondary }}
              />
              Pipeline Monitoring
            </Box>
          </Breadcrumbs>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: UI.radiusCard,
            boxShadow: '0 1px 3px rgba(26,36,45,0.06)',
            border: `1px solid ${UI.border}`,
            width: 'auto',
            background: UI.surface,
            marginX: 4,
          }}
        >
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(selectedDate)}
                onChange={(newDate) => {
                  setSelectedDate(newDate.format('YYYY-MM-DD'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: UI.radiusPill,
                      },
                      ...FILTER_INPUT_SX,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box>
            <DebounceInput
              id="sessionFilter"
              placeholder="Search sessions"
              variant="outlined"
              size="small"
              value={filterText}
              onChange={(event) => {
                setFilterText(event.target.value);
              }}
              debounceTimeout={300}
              element={TextField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{ fontSize: 18, color: UI.textMuted }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-root': { borderRadius: UI.radiusPill },
                ...FILTER_INPUT_SX,
              }}
            />
          </Box>
          <Box sx={{ width: '250px' }}>
            <Autocomplete
              multiple
              id="chain-filter"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: UI.radiusPill },
                '& .MuiInputLabel-root': { fontSize: 14 },
                ...FILTER_INPUT_SX,
              }}
              componentsProps={{
                paper: {
                  sx: {
                    '& .MuiAutocomplete-option': {
                      fontSize: 14,
                      color: colors.textGray,
                    },
                  },
                },
              }}
              options={getAllChainsFromSessionData(sessionProgressTableData)}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              value={selectedChains}
              onChange={(event, newSelectedChains) => {
                setSelectedChains(newSelectedChains);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Chain"
                  placeholder="Select Chain"
                />
              )}
            />
          </Box>
          <Box>
            <FormControlLabel
              value="end"
              control={
                <Switch
                  color="primary"
                  value={showTestSessions}
                  onChange={(e) => {
                    setShowTestSessions(!showTestSessions);
                  }}
                />
              }
              label="Show Test Sessions"
              labelPlacement="end"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: 14,
                  color: colors.textGray,
                },
              }}
            />
          </Box>

          <Box
            sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <UtcClock />

            <AlertIndicatorChip
              count={pipelineAlertsCount}
              icon={<ErrorOutlineRoundedIcon />}
              label="Pipeline Alerts"
              color="error"
              onClick={() => setServiceAlertsOpen(!serviceAlertsOpen)}
            />

            <AlertIndicatorChip
              count={sessionAlertsCount}
              icon={<WarningAmberRoundedIcon />}
              label="Session Alerts"
              color="warning"
              onClick={() => setSessionAlertsOpen(!sessionAlertsOpen)}
            />
          </Box>
        </Box>
        <Box sx={{ marginX: 4, marginY: 3 }}>
          <PipelineMonitoringTable
            cardElevation={0}
            canShowNavigationTitle={true}
            filterText={filterText}
            selectedDate={selectedDate}
            selectedChains={selectedChains}
            showTestSessions={showTestSessions}
            sxCard={{
              border: `1px solid ${UI.border}`,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
